// src/components/superadmin/OrganizationDetails.tsx
"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { api } from "../../services/Axios"
import OrganizationEditModal from './OrganizationEditModal'
import UserManagementModal from './UserManagementModal'
import PlanSelectorModal from './PlanSelectorModal'
import {
  ArrowLeft,
  Boxes,
  ArrowUpCircle,
  Users,
  RotateCw,
  Tag,
  Truck,
  Store,
  DollarSign,
  LayoutGrid,
  MinusSquare,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Building2,
  MapPin,
  Phone,
  Calendar,
  Crown,
  AlertTriangle,
} from "lucide-react"
import MessageModal from "../utils/avisos/MessageModal"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import PaymentProofModal from "./PaymentProofModal"

interface OrganizationDetailsData {
  organization: {
    _id: string
    name: string
    razonSocial: string
    NIT: string
    direccion: string
    ciudad: string
    responsableIVA: boolean
    createdAt: string
    telefonoContacto: string
    identificador?: string
    pin?: string
    setId?: string
  }
  admin: {
    _id: string
    nombre: string
    email: string
  }
  vendedores: Array<{
    _id: string
    nombre: string
    email: string
    role: string
  }>
  subscriptions: Array<{
    _id: string
    plan: string
    status: string
    startDate: string
    endDate: string
    paymentMethod: string
    paymentProof: string | null
    createdAt: string
  }>
  stats: {
    products: number
    sales: number
    vendedores: number
    categories: number
    brands: number
    suppliers: number
    sucursalCount: number
    expenses: number
    totalSales: number
    totalExpenses: number
    totalCajas: number
    cajasAbiertas: number
    cajasCerradas: number
  }
}

const OrganizationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [details, setDetails] = useState<OrganizationDetailsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState({ text: "", type: "" })
  const [showMessage, setShowMessage] = useState(false)
  const [confirmAction, setConfirmAction] = useState<{ action: string; orgId: string; userId?: string } | null>(null)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectedPaymentProof, setSelectedPaymentProof] = useState<string | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  const [showEditOrgModal, setShowEditOrgModal] = useState(false)
  const [showEditUserModal, setShowEditUserModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [userType, setUserType] = useState<'admin' | 'cajero'>('admin')
  const [showPlanSelector, setShowPlanSelector] = useState(false)

  useEffect(() => {
    const fetchOrganizationDetails = async () => {
      if (!id) return

      try {
        setLoading(true)
        const response = await api.get(`/api/admin/organization/${id}`)
        setDetails(response.data)
        setLoading(false)
      } catch (error) {
        console.error("Error al obtener detalles de la organización:", error)
        setMessage({ text: "Error al cargar los detalles de la organización", type: "error" })
        setShowMessage(true)
        setLoading(false)
      }
    }

    fetchOrganizationDetails()
  }, [id, refreshTrigger])

  const handleEditOrganization = () => {
    setShowEditOrgModal(true)
  }

  const handleSaveOrganization = async (orgId: string, data: any) => {
    try {
      await api.put(`/api/admin/organization/${orgId}`, data)
      setMessage({ text: "Organización actualizada exitosamente", type: "success" })
      setShowMessage(true)
      setShowEditOrgModal(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error: any) {
      console.error("Error al actualizar organización:", error)
      setMessage({ 
        text: error.response?.data?.msg || "Error al actualizar la organización", 
        type: "error" 
      })
      setShowMessage(true)
    }
  }

  const handleEditAdmin = () => {
    if (details?.admin) {
      setSelectedUser(details.admin)
      setUserType('admin')
      setShowEditUserModal(true)
    }
  }

  const handleEditCajero = (cajero: any) => {
    setSelectedUser(cajero)
    setUserType('cajero')
    setShowEditUserModal(true)
  }

  const handleDeleteCajero = (cajeroId: string, cajeroName: string) => {
    setConfirmAction({ action: "deleteCajero", orgId: id!, userId: cajeroId })
    setShowConfirmation(true)
  }

  const handleSaveUser = async (userId: string, data: any) => {
    try {
      const endpoint = userType === 'admin' 
        ? `/api/admin/user/admin/${userId}`
        : `/api/admin/user/cajero/${userId}`
      
      await api.put(endpoint, data)
      setMessage({ text: `${userType === 'admin' ? 'Administrador' : 'Cajero'} actualizado exitosamente`, type: "success" })
      setShowMessage(true)
      setShowEditUserModal(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error: any) {
      console.error("Error al actualizar usuario:", error)
      setMessage({ 
        text: error.response?.data?.msg || "Error al actualizar el usuario", 
        type: "error" 
      })
      setShowMessage(true)
    }
  }

  const handleActivateSubscription = () => {
    if (!id) return
    setShowPlanSelector(true)
  }

  const handleConfirmActivateWithPlan = async (plan: string) => {
    if (!id) return

    try {
      await api.post(`/api/admin/subscription/activate/${id}`, {
        plan: plan,
        months: 1,
        paymentMethod: "manual",
      })
      setMessage({ text: "Suscripción activada exitosamente", type: "success" })
      setShowMessage(true)
      setShowPlanSelector(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error) {
      console.error("Error al activar suscripción:", error)
      setMessage({ text: "Error al activar la suscripción", type: "error" })
      setShowMessage(true)
      setShowPlanSelector(false)
    }
  }

  const handleDeactivateSubscription = () => {
    if (!id || !details) return
  
    const nombreOrg = details.organization.name
  
    const clave = prompt(`⚠️ Vas a desactivar la suscripción de *${nombreOrg}*. Ingresa la clave de seguridad:`)
  
    if (clave !== "admin123") {
      alert("Clave incorrecta. Operación cancelada.")
      return
    }
  
    setConfirmAction({ action: "deactivate", orgId: id })
    setShowConfirmation(true)
  }  

  const handleResetOrganization = () => {
    if (!id || !details) return
  
    const nombreOrg = details.organization.name
  
    const clave = prompt(`⚠️ Esta acción eliminará todos los datos de la organización *${nombreOrg}*. Ingresa la clave de seguridad:`)
  
    if (clave !== "admin123") {
      alert("Clave incorrecta. Operación cancelada.")
      return
    }
  
    setConfirmAction({ action: "reset", orgId: id })
    setShowConfirmation(true)
  }  

  const handleViewPaymentProof = (proofUrl: string | null) => {
    if (proofUrl) {
      setSelectedPaymentProof(proofUrl)
    } else {
      setMessage({ text: "No hay comprobante de pago disponible", type: "error" })
      setShowMessage(true)
    }
  }

  const handleConfirmAction = async () => {
    if (!confirmAction || !id) return

    try {
      const { action, userId } = confirmAction

      if (action === "activate") {
        await api.post(`/api/admin/subscription/activate/${id}`, {
          months: 1,
          paymentMethod: "manual",
        })
        setMessage({ text: "Suscripción activada exitosamente", type: "success" })
      } else if (action === "deactivate") {
        await api.post(`/api/admin/subscription/deactivate/${id}`)
        setMessage({ text: "Suscripción desactivada exitosamente", type: "success" })
      } else if (action === "reset") {
        await api.delete(`/api/admin/organization/reset/${id}`)
        setMessage({ text: "Datos de la organización eliminados exitosamente", type: "success" })
      } else if (action === "deleteCajero" && userId) {
        await api.delete(`/api/admin/user/cajero/${userId}`)
        setMessage({ text: "Cajero eliminado exitosamente", type: "success" })
      }

      setShowMessage(true)
      setShowConfirmation(false)
      setRefreshTrigger((prev) => prev + 1)
    } catch (error) {
      console.error("Error al realizar la acción:", error)
      setMessage({ text: "Error al realizar la acción", type: "error" })
      setShowMessage(true)
      setShowConfirmation(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }  

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const isSubscriptionActive = () => {
    if (!details) return false
    return details.subscriptions.some((sub) => sub.status === "active")
  }

  const getActiveSubscription = () => {
    if (!details) return null
    return details.subscriptions.find((sub) => sub.status === "active")
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-xl shadow-sm border border-gray-200 transition-all duration-200 hover:shadow-md"
            onClick={() => navigate("/superadmin")}
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Volver</span>
          </button>
          
          <button
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-md transition-all duration-200 hover:shadow-lg"
            onClick={() => setRefreshTrigger((prev) => prev + 1)}
          >
            <RotateCw className="h-4 w-4" />
            <span className="font-medium">Actualizar</span>
          </button>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{details?.organization.name || 'Cargando...'}</h1>
              <p className="text-sm text-gray-500 mt-1">{details?.organization.NIT}</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      ) : details ? (
        <div className="space-y-6">
          {/* Row 1: Info + Admin + Subscription */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Organization Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Información
                </h2>
                <button
                  onClick={handleEditOrganization}
                  className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200"
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Razón Social</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{details.organization.razonSocial}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Ubicación</p>
                    <p className="text-sm font-medium text-gray-900">{details.organization.ciudad}</p>
                    <p className="text-xs text-gray-600 mt-1">{details.organization.direccion}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Phone className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Contacto</p>
                    <p className="text-sm font-medium text-gray-900">{details.organization.telefonoContacto}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500 mb-1">Fecha de Creación</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(details.organization.createdAt)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  {details.organization.responsableIVA ? (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      Responsable IVA
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-200 text-gray-600 text-xs font-semibold rounded-full">
                      No Responsable IVA
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Admin */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  Administrador
                </h2>
                <button
                  onClick={handleEditAdmin}
                  className="p-2 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg transition-colors duration-200"
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="text-2xl font-bold text-white">
                      {details.admin.nombre.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-bold text-gray-900 truncate">{details.admin.nombre}</p>
                    <p className="text-sm text-gray-500 truncate">{details.admin.email}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{details.vendedores.length} cajero(s) registrado(s)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                <CheckCircle className={`h-5 w-5 ${isSubscriptionActive() ? 'text-green-500' : 'text-gray-400'}`} />
                Suscripción
              </h2>
              
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${isSubscriptionActive() ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50 border-2 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Estado</span>
                    {isSubscriptionActive() ? (
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full shadow-sm">
                        ACTIVA
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-gray-400 text-white text-xs font-bold rounded-full shadow-sm">
                        INACTIVA
                      </span>
                    )}
                  </div>
                  
                  {isSubscriptionActive() && getActiveSubscription() && (
                    <div className="mt-3 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Plan</span>
                        <span className="text-sm font-bold text-gray-900">{getActiveSubscription()!.plan}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Inicio</span>
                        <span className="text-sm font-medium text-gray-700">{formatDate(getActiveSubscription()!.startDate)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Vencimiento</span>
                        <span className="text-sm font-medium text-gray-700">{formatDate(getActiveSubscription()!.endDate)}</span>
                      </div>
                      {getActiveSubscription()!.paymentProof && (
                        <button
                          className="mt-2 text-xs text-blue-600 hover:text-blue-700 underline font-medium"
                          onClick={() => handleViewPaymentProof(getActiveSubscription()!.paymentProof)}
                        >
                          Ver comprobante de pago
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="pt-2">
                  {isSubscriptionActive() ? (
                    <button
                      className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                      onClick={handleDeactivateSubscription}
                    >
                      Desactivar Suscripción
                    </button>
                  ) : (
                    <button
                      className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                      onClick={handleActivateSubscription}
                    >
                      Activar Suscripción
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <LayoutGrid className="h-5 w-5 text-purple-600" />
              Estadísticas de Uso
            </h2>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Boxes className="w-5 h-5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-600">Productos</span>
                </div>
                <p className="text-2xl font-bold text-blue-900">{details.stats.products}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpCircle className="w-5 h-5 text-green-600" />
                  <span className="text-xs font-medium text-green-600">Ventas</span>
                </div>
                <p className="text-2xl font-bold text-green-900">{details.stats.sales}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <MinusSquare className="w-5 h-5 text-red-600" />
                  <span className="text-xs font-medium text-red-600">Gastos</span>
                </div>
                <p className="text-2xl font-bold text-red-900">{details.stats.expenses}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-xs font-medium text-purple-600">Cajeros</span>
                </div>
                <p className="text-2xl font-bold text-purple-900">{details.stats.vendedores}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Store className="w-5 h-5 text-amber-600" />
                  <span className="text-xs font-medium text-amber-600">Sucursales</span>
                </div>
                <p className="text-2xl font-bold text-amber-900">{details.stats.sucursalCount}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl border border-cyan-200">
                <div className="flex items-center gap-2 mb-2">
                  <LayoutGrid className="w-5 h-5 text-cyan-600" />
                  <span className="text-xs font-medium text-cyan-600">Categorías</span>
                </div>
                <p className="text-2xl font-bold text-cyan-900">{details.stats.categories}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl border border-pink-200">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-5 h-5 text-pink-600" />
                  <span className="text-xs font-medium text-pink-600">Marcas</span>
                </div>
                <p className="text-2xl font-bold text-pink-900">{details.stats.brands}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl border border-indigo-200">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-5 h-5 text-indigo-600" />
                  <span className="text-xs font-medium text-indigo-600">Proveedores</span>
                </div>
                <p className="text-2xl font-bold text-indigo-900">{details.stats.suppliers}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl border border-teal-200">
                <div className="flex items-center gap-2 mb-2">
                  <RotateCw className="w-5 h-5 text-teal-600" />
                  <span className="text-xs font-medium text-teal-600">Total Cajas</span>
                </div>
                <p className="text-2xl font-bold text-teal-900">{details.stats.totalCajas}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-600">Abiertas</span>
                </div>
                <p className="text-2xl font-bold text-emerald-900">{details.stats.cajasAbiertas}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-5 h-5 text-slate-600" />
                  <span className="text-xs font-medium text-slate-600">Cerradas</span>
                </div>
                <p className="text-2xl font-bold text-slate-900">{details.stats.cajasCerradas}</p>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-lime-50 to-lime-100 rounded-xl border border-lime-200">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-lime-600" />
                  <span className="text-xs font-medium text-lime-600">Total Ventas</span>
                </div>
                <p className="text-xl font-bold text-lime-900">{formatCurrency(details.stats.totalSales)}</p>
              </div>
            </div>
          </div>

          {/* Cajeros Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Cajeros
                <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">
                  {details.vendedores.length}
                </span>
              </h2>
            </div>
            
            {details.vendedores.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No hay cajeros registrados</p>
                <p className="text-sm text-gray-400 mt-2">Los cajeros aparecerán aquí cuando sean agregados</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {details.vendedores.map((cajero) => (
                  <div 
                    key={cajero._id} 
                    className="group relative p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                          <span className="text-lg font-bold text-white">
                            {cajero.nombre.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-gray-900 truncate">{cajero.nombre}</h3>
                          <p className="text-xs text-gray-500 truncate">{cajero.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-1 ml-2">
                        <button
                          onClick={() => handleEditCajero(cajero)}
                          className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200 opacity-0 group-hover:opacity-100"
                          title="Editar"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteCajero(cajero._id, cajero.nombre)}
                          className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors duration-200 opacity-0 group-hover:opacity-100"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-100">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                        <Users className="w-3 h-3" />
                        Cajero
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl shadow-lg p-6 border-2 border-red-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-500 rounded-xl shadow-md">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-red-900 mb-2">Zona de Peligro</h2>
                <p className="text-sm text-red-700 mb-4">
                  Esta acción eliminará permanentemente todos los datos de la organización incluyendo productos, ventas, gastos, cajas y más. 
                  <strong className="font-bold"> Esta acción no se puede deshacer.</strong>
                </p>
                <button
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                  onClick={handleResetOrganization}
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar Todos los Datos
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Organización no encontrada</h2>
          <p className="text-gray-500 mb-6">No se pudo cargar la información de esta organización</p>
          <button
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            onClick={() => navigate("/superadmin")}
          >
            Volver al Dashboard
          </button>
        </div>
      )}

      <MessageModal
        isOpen={showMessage}
        message={message.text}
        messageType={message.type as "success" | "error" | "warning" | "info"}
        onClose={() => setShowMessage(false)}
      />

      <ConfirmationModal
        isOpen={showConfirmation}
        title={
          confirmAction?.action === "activate"
            ? `Activar Suscripción - ${details?.organization.name}`
            : confirmAction?.action === "deactivate"
              ? `Desactivar Suscripción - ${details?.organization.name}`
              : confirmAction?.action === "deleteCajero"
                ? "Eliminar Cajero"
                : `Eliminar Datos - ${details?.organization.name}`
        }
        message={
          confirmAction?.action === "activate"
            ? `¿Estás seguro de que deseas activar la suscripción de **${details?.organization.name}**?`
            : confirmAction?.action === "deactivate"
              ? `¿Estás seguro de que deseas desactivar la suscripción de **${details?.organization.name}**?`
              : confirmAction?.action === "deleteCajero"
                ? "¿Estás seguro de que deseas eliminar este cajero? Esta acción no se puede deshacer."
                : `¿Estás seguro de que deseas eliminar todos los datos de **${details?.organization.name}**? Esta acción no se puede deshacer y solo mantendrá el usuario administrador y la información básica.`
        }
        onConfirm={handleConfirmAction}
        onCancel={() => setShowConfirmation(false)}
      />

      <PaymentProofModal
        isOpen={!!selectedPaymentProof}
        imageUrl={selectedPaymentProof || ""}
        onClose={() => setSelectedPaymentProof(null)}
      />

      <PlanSelectorModal
        isOpen={showPlanSelector}
        orgName={details?.organization.name || ""}
        onConfirm={handleConfirmActivateWithPlan}
        onCancel={() => setShowPlanSelector(false)}
      />

      <OrganizationEditModal
        isOpen={showEditOrgModal}
        organization={details?.organization || null}
        onClose={() => setShowEditOrgModal(false)}
        onSave={handleSaveOrganization}
      />

      <UserManagementModal
        isOpen={showEditUserModal}
        user={selectedUser}
        onClose={() => {
          setShowEditUserModal(false)
          setSelectedUser(null)
        }}
        onSave={handleSaveUser}
        userType={userType}
      />
    </div>
  )
}

export default OrganizationDetails